// src/components/common/EditableText.tsx
// 通用 Inline 編輯組件 - 點擊即編輯，失焦自動儲存

import React, { useState, useRef, useEffect, useCallback } from 'react';

interface EditableTextProps {
  /** 當前值 */
  value: string;

  /** 儲存回調 */
  onSave: (value: string) => Promise<void>;

  /** 佔位符文字 */
  placeholder?: string;

  /** 最大字數限制 */
  maxLength?: number;

  /** 是否多行（true = textarea, false = input） */
  multiline?: boolean;

  /** 顯示模式的樣式 */
  className?: string;

  /** 編輯模式的樣式 */
  inputClassName?: string;

  /** 是否必填 */
  required?: boolean;

  /** textarea 行數（僅 multiline 時有效） */
  rows?: number;

  /** 最小高度（確保空值時也有足夠點擊區域）*/
  minHeight?: string;

  /** 開啟時直接進入編輯模式（用於 create mode 自動聚焦）*/
  initialEditMode?: boolean;

  /** blur 時即使值未變更也強制呼叫 onSave（用於 create mode 確保 task 建立）*/
  forceCallOnBlur?: boolean;
}

/**
 * 通用 Inline 編輯組件
 *
 * 特性：
 * - 點擊進入編輯模式
 * - 失焦時自動儲存（若有變更）
 * - Escape 取消編輯並恢復原值
 * - Enter 在單行模式下儲存
 */
const EditableText: React.FC<EditableTextProps> = ({
  value,
  onSave,
  placeholder = 'Click to edit...',
  maxLength,
  multiline = false,
  className = '',
  inputClassName = '',
  required = false,
  rows = 3,
  minHeight,
  initialEditMode = false,
  forceCallOnBlur = false,
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  // 使用者是否已主動輸入過：未輸入前仍可被外部 value 同步（初始 edit mode 場景）
  const hasUserTypedRef = useRef(false);

  // 當外部 value 變化時同步內部狀態
  // isEditing=true 且使用者尚未輸入（如 create mode 初始進入）時也同步
  useEffect(() => {
    if (!isEditing || !hasUserTypedRef.current) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // 進入編輯模式時自動聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // 將游標移到文字末尾
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  /**
   * 進入編輯模式
   */
  const handleClick = useCallback(() => {
    if (!isSaving) {
      setIsEditing(true);
    }
  }, [isSaving]);

  /**
   * 儲存變更
   */
  const handleSave = useCallback(async () => {
    const trimmedValue = editValue.trim();

    // 必填驗證
    if (required && !trimmedValue) {
      // 恢復原值，不儲存
      setEditValue(value);
      setIsEditing(false);
      return;
    }

    // 若值沒有變更，直接退出編輯模式（除非呼叫方要求強制 save）
    if (trimmedValue === value && !forceCallOnBlur) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      hasUserTypedRef.current = false;
      setIsEditing(false);
    } catch {
      // 儲存失敗時恢復原值
      setEditValue(value);
      hasUserTypedRef.current = false;
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, required, onSave]);

  /**
   * 取消編輯
   */
  const handleCancel = useCallback(() => {
    hasUserTypedRef.current = false;
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  /**
   * 處理按鍵事件
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter' && !multiline) {
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      handleSave();
    }
  }, [multiline, handleCancel, handleSave]);

  /**
   * 處理失焦事件
   */
  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  // 顯示模式
  if (!isEditing) {
    const displayValue = value || placeholder;
    const isPlaceholder = !value;

    return (
      <div
        onClick={handleClick}
        className={`cursor-text hover:bg-gray-50 rounded-md px-1 -mx-1 transition-colors ${className} ${
          isPlaceholder ? 'text-gray-400 italic' : ''
        }`}
        style={minHeight ? { minHeight } : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {multiline ? (
          <span className="whitespace-pre-wrap break-words">{displayValue}</span>
        ) : (
          <span className="break-words">{displayValue}</span>
        )}
      </div>
    );
  }

  // 編輯模式
  const commonProps = {
    value: editValue,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      hasUserTypedRef.current = true;
      setEditValue(e.target.value);
    },
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    maxLength,
    placeholder,
    disabled: isSaving,
    className: `w-full px-2 py-1 border border-sky-400 rounded-md
                focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-500
                disabled:opacity-50 ${inputClassName}`,
  };

  if (multiline) {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        rows={rows}
        {...commonProps}
      />
    );
  }

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type="text"
      {...commonProps}
    />
  );
};

export default EditableText;
